import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Loader,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { EMD_RISK_FLAG_LABELS } from '@seo/contracts';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import classes from './EntityPage.module.css';

const AVAILABILITY_COLOR: Record<string, string> = {
  free: 'mint',
  for_sale: 'amber',
  taken: 'graphite',
};

/**
 * EMD-генератор (§9), acceptance scenario 14.
 *
 * The risk flags are the loudest thing on the screen and the buy button refuses to work
 * without an explicit "I accept the risk". Everything else here is a list; that switch is
 * the only control in the product that spends money on something a lawyer may object to.
 */
export function EmdPage(): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [brand, setBrand] = useState('');
  const [geos, setGeos] = useState('FR,PL');
  const [zones, setZones] = useState('com,net');
  const [patterns, setPatterns] = useState('{brand}casino,{brand}-{geo}');
  const [runId, setRunId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [acceptRisk, setAcceptRisk] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmationId, setConfirmationId] = useState('');

  const run = useQuery({
    ...trpc.emd.run.queryOptions({ id: runId ?? '' }),
    enabled: runId !== null,
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.emd.pathKey() });
  };

  const generate = useMutation(
    trpc.emd.generate.mutationOptions({
      onSuccess: async (result) => {
        setRunId(result.id);
        setSelected([]);
        await invalidate();
      },
    }),
  );
  /**
   * Дві кнопки, а не одна: купівля й «купити та створити сайт» — різні наміри.
   *
   * Мокап підписує термінальну дію екрана «КУПИТИ ТА СТВОРИТИ САЙТ · 6», і вона тут є. Але
   * та сама кнопка на всі випадки коштувала б звіту: `{bought, refused, failed}` — єдине, що
   * відрізняє «конфлікт торгової марки» від «нічого не сталося», а перехід у візард змиває
   * його з екрана разом із рештою сторінки. Купівля шести доменів у пул при цьому взагалі не
   * про візард: там немає «того самого» домену, з якого починати сайт.
   *
   * Тому: `emd-purchase` купує й лишається зі звітом, `emd-purchase-create` купує й веде далі
   * — і лише коли щось справді куплено. Прогін, де всі кандидати відмовлені, не привід
   * відкривати візард із порожнім полем.
   */
  const [thenCreate, setThenCreate] = useState(false);
  const purchase = useMutation(
    trpc.emd.purchase.mutationOptions({
      onSuccess: async (result) => {
        setSelected([]);
        setConfirmationId('');
        await invalidate();
        const [first] = result.bought;
        if (thenCreate && first !== undefined) {
          await navigate({ to: '/sites/new', search: { domain: first.domain } });
        }
      },
    }),
  );

  /**
   * Decision 4 (TZ-уточнення, 2026-08-26): a purchase at or above
   * `limits.emd_purchase_dual_confirm_threshold_minor` refuses with `CONFIRMATION_REQUIRED`
   * until a second person confirms — same two-step gate bulk operations use.
   */
  const requestConfirmation = useMutation(
    trpc.emd.requestConfirmation.mutationOptions({
      onSuccess: (result) => {
        setConfirmationId(result.id);
      },
    }),
  );
  const confirmMutation = useMutation(trpc.emd.confirm.mutationOptions({ onSuccess: invalidate }));

  /**
   * Пул доменів і черга на продовження — на екрані, який у цей пул купує.
   *
   * `infra.domains.summary` і `infra.domains.expiring` існували без викликача, а план окремим
   * рядком лишає `domains.expiring` як **запобіжник**: прострочений домен знімає живий сайт із
   * інтернету. Запобіжник без екрана — це запис у базі, який ніхто не побачить, тож він тут, а
   * не в окремому реєстрі, якого мокап не малює.
   */
  const pool = useQuery(trpc.infra.domains.summary.queryOptions({ expiringWithinDays: 30 }));
  const expiring = useQuery(trpc.infra.domains.expiring.queryOptions({ withinDays: 30 }));

  const candidates = run.data?.candidates ?? [];

  const availabilitySplit = {
    free: candidates.filter((row) => row.availability === 'free').length,
    for_sale: candidates.filter((row) => row.availability === 'for_sale').length,
    taken: candidates.filter((row) => row.availability === 'taken').length,
  };

  const split = (value: string): string[] =>
    value
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part !== '');

  /**
   * A run with no geos, zones or patterns produces nothing and reports "0 candidates", which
   * reads exactly like "nothing is available". The button refuses instead.
   */
  const canGenerate =
    brand.trim() !== '' &&
    split(geos).length > 0 &&
    split(zones).length > 0 &&
    split(patterns).length > 0;

  return (
    <Stack gap="md">
      <Box className={classes.panel} data-testid="emd-form">
        <Text className={classes.panelTitle} mb={8}>
          Згенерувати кандидатів
        </Text>
        <Group gap="xs" align="flex-end">
          <TextInput
            label="Бренд"
            size="xs"
            value={brand}
            onChange={(event) => {
              setBrand(event.currentTarget.value);
            }}
            data-testid="emd-brand"
            style={{ width: 180 }}
          />
          <TextInput
            label="ГЕО (через кому)"
            size="xs"
            value={geos}
            onChange={(event) => {
              setGeos(event.currentTarget.value);
            }}
            data-testid="emd-geos"
            style={{ width: 160 }}
          />
          <TextInput
            label="Зони"
            size="xs"
            value={zones}
            onChange={(event) => {
              setZones(event.currentTarget.value);
            }}
            data-testid="emd-zones"
            style={{ width: 160 }}
          />
          <TextInput
            label="Патерни"
            size="xs"
            value={patterns}
            onChange={(event) => {
              setPatterns(event.currentTarget.value);
            }}
            data-testid="emd-patterns"
            style={{ width: 280 }}
          />
          <Button
            size="xs"
            disabled={!canGenerate}
            loading={generate.isPending}
            data-testid="emd-generate"
            onClick={() => {
              generate.mutate({
                brand,
                geos: split(geos).map((code) => ({ code })),
                zones: split(zones),
                patterns: split(patterns),
              });
            }}
          >
            Згенерувати
          </Button>
        </Group>

        {generate.error === null ? null : (
          <Text className={classes.danger} mt={8} data-testid="emd-error">
            {publicMessageOf(generate.error)}
          </Text>
        )}
      </Box>

      <Box className={classes.panel} data-testid="emd-pool">
        <Text className={classes.panelTitle} mb={8}>
          Пул доменів
        </Text>
        {pool.data === undefined ? (
          <Loader size="sm" />
        ) : (
          <Group gap="lg">
            <Text className={classes.dim}>
              усього{' '}
              <span className={classes.mono} data-testid="pool-total">
                {pool.data.total}
              </span>
            </Text>
            <Text className={classes.dim}>
              вільних під сайт{' '}
              <span className={classes.mono} data-testid="pool-usable">
                {pool.data.usable}
              </span>
            </Text>
            <Text
              className={pool.data.expiringSoon > 0 ? classes.warn : classes.dim}
              data-testid="pool-expiring"
            >
              закінчуються за 30 днів: {pool.data.expiringSoon}
            </Text>
          </Group>
        )}

        {/*
         * Перелік, а не лише число: «3 закінчуються» без імен — це тривога, з якою нічого не
         * зробиш. Домен із живим сайтом позначено окремо, бо саме він знімає сайт з інтернету.
         */}
        {(expiring.data ?? []).length === 0 ? null : (
          <Stack gap={2} mt="sm">
            {(expiring.data ?? []).slice(0, 10).map((row) => (
              <Group key={row.id} gap={8} data-testid={`pool-expiring-${row.domain}`}>
                <Text className={classes.mono}>{row.domain}</Text>
                <Text className={classes.dim}>
                  {row.expiresAt === null
                    ? 'без дати'
                    : new Date(row.expiresAt).toISOString().slice(0, 10)}
                </Text>
                {row.hasLiveSite ? (
                  <Badge size="xs" color="rose" data-testid={`pool-live-${row.domain}`}>
                    під ним живий сайт
                  </Badge>
                ) : null}
              </Group>
            ))}
          </Stack>
        )}
      </Box>

      {run.data === undefined ? (
        <Box className={classes.panel} data-testid="emd-no-run">
          <Text className={classes.dim}>Ще немає прогону — згенеруйте кандидатів.</Text>
        </Box>
      ) : (
        <Box className={classes.panel} data-testid="emd-candidates">
          <Group justify="space-between" mb={8}>
            <Group gap="sm">
              <Text className={classes.panelTitle}>
                Кандидати ·{' '}
                <span className={classes.mono} data-testid="emd-total">
                  {run.data.candidatesTotal}
                </span>
              </Text>
              {/*
               * Розклад мокапа «вільні 84 · на продажу 22 · зайняті 94». Загальне число саме
               * по собі не каже нічого: двісті кандидатів, з яких вільні три, і двісті, з яких
               * вільні сто вісімдесят, — це різні прогони й різні рішення.
               */}
              <Text className={classes.dim} data-testid="emd-availability-split">
                вільні {availabilitySplit.free} · на продажу {availabilitySplit.for_sale} · зайняті{' '}
                {availabilitySplit.taken}
              </Text>
            </Group>
            <Group gap="xs">
              <Switch
                size="xs"
                label="приймаю ризики"
                checked={acceptRisk}
                onChange={(event) => {
                  setAcceptRisk(event.currentTarget.checked);
                }}
                data-testid="emd-accept-risk"
              />
              <Button
                size="xs"
                variant="default"
                disabled={selected.length === 0}
                loading={purchase.isPending && !thenCreate}
                data-testid="emd-purchase"
                onClick={() => {
                  setThenCreate(false);
                  purchase.mutate({
                    candidateIds: selected,
                    acceptRisk,
                    ...(confirmationId === '' ? {} : { confirmationId }),
                  });
                }}
              >
                Купити в пул · {selected.length}
              </Button>
              <Button
                size="xs"
                disabled={selected.length === 0}
                loading={purchase.isPending && thenCreate}
                data-testid="emd-purchase-create"
                onClick={() => {
                  setThenCreate(true);
                  purchase.mutate({
                    candidateIds: selected,
                    acceptRisk,
                    ...(confirmationId === '' ? {} : { confirmationId }),
                  });
                }}
              >
                Купити та створити сайт · {selected.length}
              </Button>
            </Group>
          </Group>

          {/*
           * Купівля на суму від `limits.emd_purchase_dual_confirm_threshold_minor` відмовляє
           * `CONFIRMATION_REQUIRED`, доки друга особа не підтвердить: ті самі два кроки, що й
           * у масових операціях (`BulkPage`) — запит підтвердження, потім підтвердження іншим
           * актором за `confirmationId`.
           */}
          <Group gap="xs" mt="sm">
            <TextInput
              size="xs"
              placeholder="причина для підтвердження"
              value={reason}
              onChange={(event) => {
                setReason(event.currentTarget.value);
              }}
              data-testid="emd-reason"
              style={{ width: 220 }}
            />
            <Button
              size="xs"
              variant="default"
              disabled={selected.length === 0}
              loading={requestConfirmation.isPending}
              data-testid="emd-request-confirmation"
              onClick={() => {
                requestConfirmation.mutate({
                  candidateIds: selected,
                  acceptRisk,
                  reason: reason || 'покупка EMD',
                });
              }}
            >
              Запросити підтвердження
            </Button>
            <TextInput
              size="xs"
              placeholder="id підтвердження"
              value={confirmationId}
              onChange={(event) => {
                setConfirmationId(event.currentTarget.value);
              }}
              data-testid="emd-confirmation-id"
              style={{ width: 220 }}
            />
            <Button
              size="xs"
              variant="default"
              disabled={confirmationId === ''}
              loading={confirmMutation.isPending}
              data-testid="emd-confirm"
              onClick={() => {
                confirmMutation.mutate({ id: confirmationId });
              }}
            >
              Підтвердити
            </Button>
          </Group>

          {requestConfirmation.error === null ? null : (
            <Text className={classes.danger} mt={8} data-testid="emd-request-confirmation-error">
              {publicMessageOf(requestConfirmation.error)}
            </Text>
          )}
          {confirmMutation.error === null ? null : (
            <Text className={classes.danger} mt={8} data-testid="emd-confirm-error">
              {publicMessageOf(confirmMutation.error)}
            </Text>
          )}

          {/*
           * Відмова купівлі мусить бути на екрані.
           *
           * Блок нижче показує лише успіх, і без цього рядка провалена мутація виглядала б
           * як «нічого не сталося» — на дії, яка витрачає гроші реєстратора. Помітно це
           * тільки живому оператору: тест шукає успіх і порожнього місця не бачить.
           */}
          {purchase.error === null ? null : (
            <Text className={classes.danger} mb="sm" data-testid="emd-purchase-error" role="alert">
              {publicMessageOf(purchase.error)}
            </Text>
          )}

          {purchase.data === undefined ? null : (
            <Group gap="lg" mb="sm">
              <Text className={classes.dim}>
                куплено{' '}
                <span className={classes.mono} data-testid="emd-bought">
                  {purchase.data.bought.length}
                </span>
              </Text>
              <Text className={classes.warn} data-testid="emd-refused">
                відмовлено: {purchase.data.refused.length}
                {purchase.data.refused.length === 0
                  ? ''
                  : ` — ${purchase.data.refused.map((row) => `${row.domain}: ${row.reason}`).join('; ')}`}
              </Text>
              {purchase.data.failed.length === 0 ? null : (
                <Text className={classes.danger} data-testid="emd-failed">
                  помилок: {purchase.data.failed.length}
                </Text>
              )}
            </Group>
          )}

          <Table striped withRowBorders={false} verticalSpacing={4} fz="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th />
                <Table.Th>Домен</Table.Th>
                <Table.Th>ГЕО</Table.Th>
                <Table.Th>Стан</Table.Th>
                <Table.Th>Скор</Table.Th>
                <Table.Th>Ціна</Table.Th>
                <Table.Th>Ризики</Table.Th>
                <Table.Th>Реєстратор</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {candidates.map((candidate) => (
                <Table.Tr key={candidate.id} data-testid={`emd-row-${candidate.domain}`}>
                  <Table.Td>
                    <Checkbox
                      size="xs"
                      checked={selected.includes(candidate.id)}
                      disabled={candidate.purchasedDomainId !== null}
                      data-testid={`emd-pick-${candidate.domain}`}
                      onChange={(event) => {
                        const { checked } = event.currentTarget;
                        setSelected((current) =>
                          checked
                            ? [...current, candidate.id]
                            : current.filter((id) => id !== candidate.id),
                        );
                      }}
                    />
                  </Table.Td>
                  <Table.Td className={classes.mono}>{candidate.domain}</Table.Td>
                  <Table.Td className={classes.mono} data-testid={`emd-geo-${candidate.domain}`}>
                    {candidate.geoCode ?? '—'}
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      size="xs"
                      color={AVAILABILITY_COLOR[candidate.availability] ?? 'graphite'}
                      data-testid={`emd-availability-${candidate.domain}`}
                    >
                      {candidate.availability}
                    </Badge>
                  </Table.Td>
                  <Table.Td className={classes.mono} data-testid={`emd-score-${candidate.domain}`}>
                    {candidate.score}
                  </Table.Td>
                  {/*
                   * Ціна вирішує не менше за скор: $12 і $4 500 у мокапа стоять поруч, і
                   * без неї «купити 6» означає підписатись на суму, якої не показали.
                   */}
                  <Table.Td className={classes.mono} data-testid={`emd-price-${candidate.domain}`}>
                    {candidate.price === null
                      ? '—'
                      : `${(candidate.price / 100).toFixed(2)} ${candidate.priceCurrency}`}
                  </Table.Td>
                  <Table.Td data-testid={`emd-risks-${candidate.domain}`}>
                    {candidate.riskFlags.length === 0 ? (
                      <Text className={classes.dim}>—</Text>
                    ) : (
                      <Group gap={4}>
                        {candidate.riskFlags.map((flag) => (
                          <Badge key={flag} size="xs" color="rose">
                            {EMD_RISK_FLAG_LABELS[flag] ?? flag}
                          </Badge>
                        ))}
                      </Group>
                    )}
                  </Table.Td>
                  <Table.Td className={classes.dim}>{candidate.registrar ?? '—'}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      )}
    </Stack>
  );
}
